import { ReactNode, useContext, useMemo } from "react";

import { Form, Typography } from "antd";
import { FormItemProps as AntFormItemProps } from "antd/lib/form";
import { FormContext } from "antd/lib/form/context";
import { clsx } from "clsx";
import { FieldContext } from "rc-field-form";

import { getFormItemTooltipConfig } from "./util";

import styles from "./RVFormItem.module.scss";

function hasRequiredRule(rules: AntFormItemProps["rules"]): boolean {
    if (!rules?.length) {
        return false;
    }

    return rules.some((rule) => {
        if (typeof rule === "function") {
            return false;
        }

        return "required" in rule && !!rule.required;
    });
}

export type RVFormItemProps = AntFormItemProps & {
    /** Temp: Indicates if the form item is part of a full-page form layout */
    isFullPageForm?: boolean;
    /** Specifies no padding under the form item */
    noPadding?: boolean;
    /** Description displayed in between label and input */
    description?: ReactNode;
    /** Test id forwarded to the outer Form.Item for testing purposes */
    "data-testid"?: string | string[];
};

/**
 * RVFormItem
 *
 * A wrapper around Ant Design's Form.Item component with standardized handling of tooltip and spacing.
 * It supports large spacing for full-page forms, as a temp solution to easily roll it out without
 * affecting existing RVForms.
 *
 * - Provides consistent typing and TypeScript support
 * - Automatically standardizes tooltip appearance using getFormItemTooltipConfig
 * - Handles tooltip positioning with required field markers
 * - Can be used as a drop-in replacement for Form.Item
 * - Allows for future customization without changing component usage
 *
 * @example
 * // Basic usage
 * <RVFormItem name="username" label="Username">
 *   <Input />
 * </RVFormItem>
 *
 * @example
 * // With validation
 * <RVFormItem
 *   name="email"
 *   label="Email"
 *   rules={[
 *     { required: true, message: 'Please enter your email' },
 *     { type: 'email', message: 'Please enter a valid email' }
 *   ]}
 * >
 *   <Input />
 * </RVFormItem>
 *
 * @example
 * // With tooltip (automatically standardized)
 * <RVFormItem
 *   name="apiKey"
 *   label="API Key"
 *   tooltip="Found in your account settings"
 *   extra="Keep this secret and never share it publicly"
 * >
 *   <Input.Password />
 * </RVFormItem>
 *
 * @param {RVFormItemProps} props - Component props (extends antd FormItemProps)
 * @returns {JSX.Element}
 */
export function RVFormItem({
    label,
    help,
    extra,
    description,
    hidden,
    tooltip,
    required,
    noStyle,
    isFullPageForm = false,
    noPadding = false,
    className,
    validateStatus,
    hasFeedback,
    "data-testid": dataTestId,
    ...restProps
}: RVFormItemProps): JSX.Element {
    // Determine if the field is required either from the 'required' prop or from validation rules
    const effectiveRequired = useMemo(
        () => (required !== undefined ? required : hasRequiredRule(restProps.rules)),
        [required, restProps.rules]
    );

    // Automatically standardize tooltip configuration
    const tooltipConfig = useMemo(
        () =>
            tooltip && typeof tooltip === "string"
                ? getFormItemTooltipConfig(tooltip, effectiveRequired)
                : tooltip
                  ? getFormItemTooltipConfig(tooltip as JSX.Element, effectiveRequired)
                  : undefined,
        [tooltip, effectiveRequired]
    );

    // Replicate AntD's htmlFor derivation so the outer label's `for` matches
    // the `id` that the inner noStyle Form.Item sets on the input.
    // This accounts for Form.List prefix paths and the Form's name prop.
    const { prefixName } = useContext(FieldContext);
    const { name: formName } = useContext(FormContext);

    const htmlFor = useMemo(() => {
        if (restProps.htmlFor) {
            return restProps.htmlFor;
        }
        if (restProps.name === undefined) {
            return undefined;
        }
        const nameArr = Array.isArray(restProps.name) ? restProps.name : [restProps.name];
        const prefixArr = prefixName ? (Array.isArray(prefixName) ? prefixName : [prefixName]) : [];
        const fullPath = [...prefixArr, ...nameArr];
        if (!fullPath.length) {
            return undefined;
        }
        const mergedId = fullPath.join("_");
        return formName ? `${formName}_${mergedId}` : mergedId;
    }, [restProps.htmlFor, restProps.name, prefixName, formName]);

    return (
        // eslint-disable-next-line no-restricted-syntax -- RVFormItem wraps Form.Item by design
        <Form.Item
            label={label}
            htmlFor={htmlFor}
            help={help}
            extra={extra}
            hidden={hidden}
            required={effectiveRequired}
            tooltip={tooltipConfig}
            noStyle={noStyle}
            validateStatus={validateStatus}
            hasFeedback={hasFeedback}
            data-testid={dataTestId}
            className={clsx(styles.formItemContainer, className, {
                [styles.largeFormSpacing]: isFullPageForm,
                [styles.noPadding]: noPadding,
            })}
        >
            {description && <Typography.Text>{description}</Typography.Text>}
            {/* eslint-disable-next-line no-restricted-syntax -- inner Form.Item for value binding */}
            <Form.Item {...restProps} noStyle />
        </Form.Item>
    );
}
